import React, { useEffect, useState, useRef, ReactElement } from 'react';
import { Instance, ToolbarItem, default as PSPDFKitModule } from 'pspdfkit';

import {
  getFieldValue,
  getUpdatedAt,
} from '@upstart/harmony/lib/form-utils.js';
import {
  CloudDoneOutlinedIcon,
  CloudOffOutlinedIcon,
  CloudSyncOutlinedIcon,
} from '~/index.ts';
import { Car } from '@upstart/harmony/lib/legos/carTypes.js';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes.js';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes.js';
import { User } from '@upstart/harmony/lib/legos/userTypes.js';
import { z } from 'zod';

const logger = {
  exception: (): void => {},
};

// @todo move outside
export const editPdfRequestSchema = z.object({
  dealId: z.string(),
  documentId: z.string().uuid().nullish(),
  url: z.string().url(),
  templateId: z.string().optional(),
  normalizeTextInputs: z.boolean(),
});

export type EditPdfRequestSchema = z.infer<typeof editPdfRequestSchema>;

// We use this to fill up form data, we do not need all properties of user/deal/dealer/car and it would be better to strip sensitive information
export interface PreviewData {
  user?: User;
  deal?: Deal;
  dealer?: Dealer;
  car?: Car;
}

export enum CloudSaveState {
  DEFAULT = 'default',
  SAVING = 'saving',
  SAVED = 'saved',
  FAILED = 'failed',
}

export const CloudSaveStatus: React.FC<{
  state: CloudSaveState;
  labels: {
    savingLabel: string;
    savedLabel: string;
    saveFailureLabel: string;
  };
  statusContainerClass?: string;
  errorContainerClass?: string;
  labelClass?: string;
}> = ({
  state,
  labels: { savingLabel, savedLabel, saveFailureLabel },
  statusContainerClass,
  errorContainerClass,
  labelClass,
}) => {
  const renderStatus = (): ReactElement => {
    switch (state) {
      case CloudSaveState.SAVING:
        return (
          <>
            <div className={labelClass}>{savingLabel}</div>
            <CloudSyncOutlinedIcon
              data-test-id="saving-icon"
              fontSize="inherit"
            />
          </>
        );
      case CloudSaveState.SAVED:
        return (
          <>
            <div className={labelClass}>{savedLabel}</div>
            <CloudDoneOutlinedIcon
              data-test-id="saved-icon"
              fontSize="inherit"
            />
          </>
        );
      case CloudSaveState.FAILED:
        return (
          <div className={errorContainerClass}>
            <div className={labelClass}>{saveFailureLabel}</div>
            <CloudOffOutlinedIcon
              color={'error'}
              data-test-id="failed-icon"
              fontSize="inherit"
            />
          </div>
        );
      default:
        return (
          <CloudDoneOutlinedIcon
            data-test-id="default-icon"
            fontSize="inherit"
          />
        );
    }
  };

  return <div className={statusContainerClass}>{renderStatus()}</div>;
};

interface SessionProps {
  documentId: string;
  serverUrl: string;
  authPayload: {
    jwt: string;
  };
}

export interface PdfEditorProps extends SessionProps {
  onLoad: (arg: { instance: Instance }) => void;
  onPrint: VoidFunction;
  previewData?: PreviewData;
  label?: string;
  readOnly?: boolean;
  noPrintIcon?: boolean;
  labels: {
    savingLabel: string;
    savedLabel: string;
    saveFailureLabel: string;
  };
  containerClass?: string;
  headerClass?: string;
  pspdfkitContainerClass?: string;
  cloudStatusLabelClass?: string;
  cloudStatusContainerClass?: string;
}

// shared-components for some reason cannot get the default export from pspdfkit. It's treated as if its exporting the entire rexport of psdpfkit
declare type PSPDFKitModuleType = typeof PSPDFKitModule.default;
declare global {
  interface Window {
    PSPDFKit: PSPDFKitModuleType;
  }
}

const getStylesheets = (): string[] => [
  [
    `${window.location.protocol}//${window.location.host}`,
    'pspdfkit-lib',
    'custom-styles.css',
  ].join('/'),
];

const CHECKED_CHECKBOX_VALUES = ['true', 'selected'];
const CHECKBOX_VALUES = [...CHECKED_CHECKBOX_VALUES, 'false'];

const prefillDoc = async ({
  instance,
  previewData,
}: {
  instance: Instance;
  previewData: PreviewData;
}): Promise<void> => {
  const formFields = await instance.getFormFields();
  const formValuesArray = await Promise.all(
    formFields.map(async (field) => {
      const name = field.name;
      if (name.startsWith('deal.updatedAt')) {
        const updatedAt = getUpdatedAt(name);
        if (updatedAt) {
          return { [name]: updatedAt };
        }
      }

      const value = await getFieldValue(
        name,
        previewData as Record<string, unknown>,
      );

      if (name.split('==').length === 2 && CHECKBOX_VALUES.includes(value)) {
        return {
          [name]: [CHECKED_CHECKBOX_VALUES.includes(value) ? 'Yes' : 'No'],
        };
      }

      if (!value) {
        return undefined;
      }

      return { [name]: value };
    }),
  );

  const formValues = Object.assign({}, ...formValuesArray);
  instance.setFormFieldValues(formValues);
};

// @todo update this to do a fetch request to edit pdf
export const startPdfEditSession = (
  props: EditPdfRequestSchema,
): Promise<SessionProps> =>
  Promise.resolve({
    documentId: props.documentId,
    serverUrl: 'test',
    authPayload: {
      jwt: 'test',
    },
  }) as Promise<SessionProps>;

interface ToolBar {
  icon: string;
  id: string;
  title: string;
  type: string;
}

type CustomToolbarActionHandler = (arg: {
  instance: Instance;
  onPress: VoidFunction;
  noPrintIcon: boolean;
}) => void;

export const ToolBarItem: { print: ToolBar; download: ToolBar } = {
  print: {
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false"><path d="M22.5 9v9a1.5 1.5 0 01-1.5 1.5h-2.5v-2h-13v2H3A1.5 1.5 0 011.5 18V9A1.5 1.5 0 013 7.5h2.5v2h13v-2H21A1.5 1.5 0 0122.5 9z" stroke="currentColor" stroke-miterlimit="10" fill="none"></path><path opacity=".25" d="M22 15v3a1 1 0 01-1 1h-2v-2H5v2H3a1 1 0 01-1-1v-3h20z" fill="currentColor"></path><path opacity=".5" d="M19.5 13.5a1 1 0 100-2 1 1 0 000 2z" fill="currentColor"></path><path d="M18.5 18v3.5h-13V18M5.5 9V2.5h13V9" stroke="currentColor" fill="none"></path></svg>',
    id: 'print-pdf',
    title: 'Print',
    type: 'print',
  },
  download: {
    icon: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false"><path d="M3.5 17.5V19C3.5 19.8284 4.17157 20.5 5 20.5H20C20.8284 20.5 21.5 19.8284 21.5 19V17.5" stroke="currentColor" stroke-linecap="round"></path><path opacity="0.75" d="M12.5 16L12.1464 16.3536L12.5 16.7071L12.8536 16.3536L12.5 16ZM13 4C13 3.72386 12.7761 3.5 12.5 3.5C12.2239 3.5 12 3.72386 12 4H13ZM9.35355 12.1464C9.15829 11.9512 8.84171 11.9512 8.64645 12.1464C8.45118 12.3417 8.45118 12.6583 8.64645 12.8536L9.35355 12.1464ZM16.3536 12.8536C16.5488 12.6583 16.5488 12.3417 16.3536 12.1464C16.1583 11.9512 15.8417 11.9512 15.6464 12.1464L16.3536 12.8536ZM13 16V4H12V16H13ZM8.64645 12.8536L12.1464 16.3536L12.8536 15.6464L9.35355 12.1464L8.64645 12.8536ZM12.8536 16.3536L16.3536 12.8536L15.6464 12.1464L12.1464 15.6464L12.8536 16.3536Z" fill="currentColor"></path></svg>',
    id: 'download-pdf',
    title: 'Download',
    type: 'export-pdf',
  },
};

/**
 * 1. Replace the print icon with custom
 * >> Note: Library has nice print functionaly with progress indicator. However,
 * >> this is inconsistent across browsers and causes lot of complaints on iPad.
 * >> Hence, the override
 */
const customizeToolBar: CustomToolbarActionHandler = ({
  instance,
  noPrintIcon,
  onPress,
}) => {
  instance.setToolbarItems((items) => {
    const toolBars = items.map((item) => {
      switch (item.type) {
        case 'export-pdf':
          return null;
        case 'print':
          return noPrintIcon
            ? null
            : {
                ...ToolBarItem.print,
                type: 'custom',
                onPress,
              };
        default:
          return item;
      }
    });
    return toolBars.filter((item) => !!item) as ToolbarItem[];
  });
};

/**
 * Script is only loaded in browser, confirm that PSPDFKit is defined
 */
const isPspdfkitReady = (): boolean =>
  typeof window !== 'undefined' && !!window.PSPDFKit;

export const PdfEditor: React.FC<PdfEditorProps> = ({
  authPayload,
  documentId,
  onLoad,
  onPrint: onPress,
  previewData,
  serverUrl,
  label,
  readOnly,
  noPrintIcon,
  labels,
  containerClass,
  headerClass,
  pspdfkitContainerClass,
  cloudStatusLabelClass,
  cloudStatusContainerClass,
}) => {
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef(null);
  const [documentState, setDocumentState] = useState(CloudSaveState.DEFAULT);

  useEffect(() => {
    setLoaded(false);
    const checkScriptLoadStatus = setInterval(() => {
      if (isPspdfkitReady()) {
        setLoaded(true);
        clearInterval(checkScriptLoadStatus);
      }
    }, 100);

    return () => {
      clearInterval(checkScriptLoadStatus);
    };
  }, []);

  useEffect(() => {
    let pdfKitInstance: Instance;
    const didSaveEvent = (response: { error: Error }): void => {
      const navigator = window?.navigator;
      if ((navigator && !navigator.onLine) || response?.error) {
        setDocumentState(CloudSaveState.FAILED);
      } else {
        setDocumentState(CloudSaveState.SAVED);
      }
    };
    const willSaveEvent = (): void => {
      setDocumentState(CloudSaveState.SAVING);
    };
    const container = containerRef.current;
    const windowPSPDFKit: PSPDFKitModuleType = window.PSPDFKit;
    if (container && documentId && loaded && window.PSPDFKit) {
      const viewState = window.PSPDFKit.ViewState;
      windowPSPDFKit.unload(container);
      windowPSPDFKit
        .load({
          serverUrl,
          container,
          documentId,
          authPayload,
          instant: true,
          styleSheets: getStylesheets(),
          initialViewState: new viewState({
            readOnly: !!readOnly,
          }),
        })
        .then(async (instance: Instance) => {
          pdfKitInstance = instance;
          onLoad({ instance });
          customizeToolBar({
            instance,
            onPress,
            noPrintIcon: !!noPrintIcon,
          });
          if (previewData) {
            await prefillDoc({ instance, previewData });
          }

          setDocumentState(CloudSaveState.DEFAULT);

          instance.addEventListener('formFieldValues.didSave', didSaveEvent);

          instance.addEventListener('formFieldValues.willSave', willSaveEvent);
        })
        .catch(logger.exception);
    }
    return () => {
      if (pdfKitInstance?.removeEventListener) {
        pdfKitInstance.removeEventListener(
          'formFieldValues.didSave',
          didSaveEvent,
        );
        pdfKitInstance.removeEventListener(
          'formFieldValues.willSave',
          willSaveEvent,
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, loaded]);

  return (
    <div className={containerClass}>
      <div className={headerClass}>
        <div>{label}</div>
        <CloudSaveStatus
          state={documentState}
          labels={labels}
          labelClass={cloudStatusLabelClass}
          statusContainerClass={cloudStatusContainerClass}
        />
      </div>
      <div className={pspdfkitContainerClass} ref={containerRef} />
    </div>
  );
};
